<template>
  <article class="gs-video-card" :class="{ 'is-draft': !video.is_published }">
    <div class="gs-video-card__media">
      <div
        v-if="thumbUrl"
        class="gs-video-card__thumb"
        :style="thumbStyle"
        role="img"
        :aria-label="video.name"
      ></div>
      <div v-else class="gs-video-card__thumb gs-video-card__thumb--empty" role="img" :aria-label="video.name">
        <gs-icon name="play" size="26" />
      </div>
      <div class="gs-video-card__pills">
        <gs-badge v-if="video.is_published" variant="success">Published</gs-badge>
        <gs-badge v-else class="gs-video-card__pill--muted">Draft</gs-badge>
        <gs-badge class="gs-video-card__source">{{ source }}</gs-badge>
        <gs-badge v-if="processing" class="gs-video-card__pill--processing">
          <span class="gs-video-card__pulse" aria-hidden="true"></span>
          Processing
        </gs-badge>
      </div>
      <span v-if="duration" class="gs-video-card__duration">{{ duration }}</span>
      <div class="gs-video-card__scrim" aria-hidden="true"></div>
      <div class="gs-video-card__actions">
        <button
          type="button"
          class="gs-video-card__play"
          data-action="preview"
          aria-label="Preview video"
          @click="$emit('preview', video)"
        >
          <gs-icon name="play" size="22" />
        </button>
        <div class="gs-video-card__quick">
          <gs-button
            variant="ghost"
            size="sm"
            data-action="edit"
            aria-label="Edit"
            title="Edit"
            @click="$emit('edit', video)"
          >
            <gs-icon name="edit" size="16" />
          </gs-button>
          <gs-button
            variant="ghost"
            size="sm"
            data-action="analytics"
            aria-label="Analytics"
            title="Analytics"
            @click="$emit('analytics', video)"
          >
            <gs-icon name="chart" size="16" />
          </gs-button>
          <gs-button
            variant="ghost"
            size="sm"
            data-action="share"
            aria-label="Share &amp; embed"
            title="Share &amp; embed"
            @click="$emit('share', video)"
          >
            <gs-icon name="share" size="16" />
          </gs-button>
        </div>
      </div>
    </div>
    <div class="gs-video-card__body">
      <div class="gs-video-card__title-row">
        <h3 class="gs-video-card__title" :title="video.name">{{ video.name }}</h3>
        <dropmenu :items="menuItems" :menu-items="menuItems" small data-menu>
          <gs-icon name="ellipsis" size="16" />
        </dropmenu>
      </div>
      <p class="gs-video-card__meta">Updated {{ video.readable_updated_at }}</p>
      <span v-if="categoryName" class="gs-video-card__chip">{{ categoryName }}</span>
    </div>
  </article>
</template>

<script>
import Dropmenu from '@groovepages/gd-ui-kit/components/DropMenu.vue';
import { formatDuration } from '@/utils/duration';

// type 2 -> YouTube, type 1 -> legacy direct upload, type 4 -> Mux, everything else is a
// plain link/MP4 source. Exported so the library grid can reuse the same
// mapping without duplicating it.
export function sourceLabel(video) {
  const type = video && video.video_source_type;
  if (type === 2) return 'YouTube';
  if (type === 1) return 'Legacy upload';
  if (Number(type) === 4) return 'Mux';
  return 'Link';
}

export default {
  name: 'VideoCard',
  components: { Dropmenu },
  props: {
    video: {
      type: Object,
      required: true,
    },
    categoryName: {
      type: String,
      default: '',
    },
  },
  computed: {
    source() {
      return sourceLabel(this.video);
    },
    processing() {
      return !!(this.video.is_processing || this.video.is_uploading || this.video.temp_video_source);
    },
    duration() {
      return this.video.video_duration && !isNaN(this.video.video_duration)
        ? formatDuration(this.video.video_duration)
        : '';
    },
    // An empty `thumbnail` used to render `url("")`, which makes the browser
    // fetch the current document as an image; the card shows a placeholder
    // instead now.
    thumbUrl() {
      return typeof this.video.thumbnail !== 'undefined' && this.video.thumbnail !== ''
        ? this.video.thumbnail
        : '';
    },
    thumbStyle() {
      return {
        backgroundImage: this.backgroundImageUrl(this.thumbUrl),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },
    menuItems() {
      return [
        { text: 'Get video', callback: () => this.$emit('share', this.video) },
        { text: 'Edit', callback: () => this.$emit('edit', this.video) },
        { text: 'Analytics', callback: () => this.$emit('analytics', this.video) },
        { text: 'Clone', callback: () => this.$emit('clone', this.video) },
        { text: 'Delete', callback: () => this.$emit('delete', this.video) },
      ];
    },
  },
  methods: {
    backgroundImageUrl(url) {
      return `url("${url}")`;
    },
  },
};
</script>

<style scoped>
.gs-video-card {
  display: flex;
  position: relative;
  flex-direction: column;
  /* The ellipsis menu is a descendant, not a portal. Clipping the card cuts
     off Clone and Delete, which sit at the bottom of that menu. */
  overflow: visible;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
  transition: box-shadow .15s ease;
}

.gs-video-card:hover,
.gs-video-card:focus-within {
  z-index: 4;
  box-shadow: var(--gs-shadow-md);
}

.gs-video-card.is-draft {
  border-style: dashed;
  border-color: var(--gs-border-strong);
}

.gs-video-card__media {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-lg) var(--gs-radius-lg) 0 0;
}

.gs-video-card__thumb {
  position: absolute;
  inset: 0;
  background-repeat: no-repeat;
}

.gs-video-card__thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
}

.gs-video-card__pills {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.gs-video-card__pill--muted {
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border-color: var(--gs-border);
}

/* Tinted from `--gs-primary` through a `currentColor` wash (the StatCard
   technique) so the pill follows the brand token instead of pinning a copy of
   today's pink: a 12% fill and a 28% ring, both painted behind the label. */
.gs-video-card__pill--processing {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--gs-primary);
  background: transparent;
  border-color: transparent;
}

.gs-video-card__pill--processing::before,
.gs-video-card__pill--processing::after {
  content: '';
  position: absolute;
  top: -1px;
  right: -1px;
  bottom: -1px;
  left: -1px;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
}

.gs-video-card__pill--processing::before {
  background: currentColor;
  opacity: .12;
}

.gs-video-card__pill--processing::after {
  box-shadow: inset 0 0 0 1px currentColor;
  opacity: .28;
}

.gs-video-card__pulse {
  width: 6px;
  height: 6px;
  background: var(--gs-primary);
  border-radius: 999px;
  animation: gs-video-card-pulse 1.4s ease-in-out infinite;
}

@keyframes gs-video-card-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .35; }
}

.gs-video-card__source {
  color: rgba(255, 255, 255, .95);
  background: rgba(15, 23, 42, .65);
  border-color: transparent;
}

.gs-video-card__duration {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 2;
  padding: 2px 7px;
  color: rgba(255, 255, 255, .95);
  background: rgba(15, 23, 42, .65);
  border-radius: var(--gs-radius-sm);
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.gs-video-card__scrim {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(to top, rgba(15, 23, 42, .55), rgba(15, 23, 42, 0) 55%);
  opacity: 0;
  transition: opacity .15s ease;
}

.gs-video-card__actions {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  opacity: 0;
  transition: opacity .15s ease;
}

.gs-video-card:hover .gs-video-card__scrim,
.gs-video-card:focus-within .gs-video-card__scrim {
  opacity: 1;
}

.gs-video-card:hover .gs-video-card__actions,
.gs-video-card:focus-within .gs-video-card__actions {
  opacity: 1;
  pointer-events: auto;
}

.gs-video-card__play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  color: rgba(255, 255, 255, .96);
  background: rgba(15, 23, 42, .55);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.gs-video-card__play:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-video-card__quick {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  gap: 6px;
}

.gs-video-card__quick ::v-deep .gs-button {
  color: rgba(255, 255, 255, .96);
  background: rgba(15, 23, 42, .55);
  border-color: transparent;
}

.gs-video-card__quick ::v-deep .gs-button:hover {
  background: rgba(15, 23, 42, .75);
}

.gs-video-card__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px 16px;
  background: var(--gs-surface);
  border-radius: 0 0 var(--gs-radius-lg) var(--gs-radius-lg);
}

/* The trigger sits on the card's right edge. Open the menu inward so the
   library's horizontal clip does not slice it off. */
.gs-video-card__title-row ::v-deep .select-none .absolute {
  right: 0;
  left: auto;
  z-index: 6;
}

.gs-video-card__title-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  justify-content: space-between;
}

.gs-video-card__title {
  margin: 0;
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gs-video-card__meta {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-video-card__chip {
  display: inline-flex;
  align-self: flex-start;
  padding: 2px 9px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}
</style>
